import React, { useState, useEffect, useRef, useCallback } from 'react';
import { supabase } from '../supabase';
import { createCompanyInvite, getCompanyInvites, revokeCompanyInvite } from '../db';

// ── Constants ─────────────────────────────────────────────────────────────────

const ROLE_META = {
  owner:  { label: 'Owner',  color: 'var(--accent)',  bg: 'var(--blue-bg)',  border: 'rgba(45,125,210,0.3)'  },
  admin:  { label: 'Admin',  color: 'var(--amber)',   bg: 'var(--amber-bg)', border: 'rgba(245,158,11,0.3)'  },
  member: { label: 'Member', color: 'var(--text3)',   bg: 'var(--surface2)', border: 'var(--border)'         },
};

// ── Subcomponents ─────────────────────────────────────────────────────────────

function RoleBadge({ role }) {
  const m = ROLE_META[role] || ROLE_META.member;
  return (
    <span style={{
      fontSize: 10, fontWeight: 700, fontFamily: 'var(--mono)', textTransform: 'uppercase',
      letterSpacing: '0.04em', padding: '2px 7px', borderRadius: 6, flexShrink: 0,
      color: m.color, background: m.bg, border: `1px solid ${m.border}`,
    }}>
      {m.label}
    </span>
  );
}

function Avatar({ name, role, size = 40 }) {
  const bg    = role === 'owner' ? 'var(--accent)' : role === 'admin' ? 'var(--amber-bg)' : 'var(--surface2)';
  const color = role === 'owner' ? '#fff' : role === 'admin' ? 'var(--amber)' : 'var(--text2)';
  const meta  = ROLE_META[role] || ROLE_META.member;
  return (
    <div style={{
      width: size, height: size, borderRadius: '50%', flexShrink: 0,
      background: bg, border: `1px solid ${meta.border}`,
      display: 'flex', alignItems: 'center', justifyContent: 'center',
      fontSize: Math.round(size * 0.38), fontWeight: 700, color,
    }}>
      {(name || '?').charAt(0).toUpperCase()}
    </div>
  );
}

function Toast({ message, type, onDismiss }) {
  useEffect(() => {
    const t = setTimeout(onDismiss, 3000);
    return () => clearTimeout(t);
  }, [onDismiss]);

  return (
    <div style={{
      position: 'fixed', bottom: 24, right: 24, zIndex: 9999,
      background: type === 'error' ? 'var(--red-bg)' : 'var(--green-bg)',
      border: `1px solid ${type === 'error' ? 'rgba(239,68,68,0.4)' : 'rgba(29,184,122,0.4)'}`,
      color: type === 'error' ? 'var(--red)' : 'var(--green)',
      borderRadius: 8, padding: '12px 16px', fontSize: 13, fontWeight: 500,
      boxShadow: '0 4px 24px rgba(0,0,0,0.25)', maxWidth: 320,
      display: 'flex', alignItems: 'center', gap: 10,
    }}>
      {type === 'error' ? (
        <svg width="14" height="14" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2.2" strokeLinecap="round" strokeLinejoin="round" style={{ flexShrink: 0 }}>
          <circle cx="12" cy="12" r="10"/><line x1="12" y1="8" x2="12" y2="12"/><line x1="12" y1="16" x2="12.01" y2="16"/>
        </svg>
      ) : (
        <svg width="14" height="14" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2.2" strokeLinecap="round" strokeLinejoin="round" style={{ flexShrink: 0 }}>
          <path d="M20 6L9 17l-5-5"/>
        </svg>
      )}
      {message}
    </div>
  );
}

// ── Invite Modal ───────────────────────────────────────────────────────────────

function InviteModal({ companyId, companyName, session, profile, seatsFull, onClose, onInvited }) {
  const [name, setName]       = useState('');
  const [email, setEmail]     = useState('');
  const [role, setRole]       = useState('member');
  const [sending, setSending] = useState(false);
  const [error, setError]     = useState('');
  const [success, setSuccess] = useState(false);

  async function handleSubmit(e) {
    e.preventDefault();
    if (!email.trim()) { setError('Email address is required.'); return; }
    if (seatsFull)     { setError('Seat limit reached. Upgrade to add more members.'); return; }
    setSending(true);
    setError('');

    // 1. Insert pending invite via db.js
    const result = await createCompanyInvite(companyId, email.trim(), role, session?.user?.id, []);
    if (!result) {
      setError('Failed to create invite. The email may already have a pending invite.');
      setSending(false);
      return;
    }

    const inviteLink = `${window.location.origin}?company_invite=${result.token}`;

    // 2. Fetch company code for the email
    let companyCode = '';
    try {
      const { data: co } = await supabase
        .from('companies')
        .select('company_code')
        .eq('id', companyId)
        .single();
      companyCode = co?.company_code || '';
    } catch { /* non-critical */ }

    // 3. Call Edge Function (non-blocking)
    try {
      await fetch(
        `${process.env.REACT_APP_SUPABASE_URL}/functions/v1/send-invite`,
        {
          method: 'POST',
          headers: {
            'Content-Type': 'application/json',
            'Authorization': `Bearer ${session?.access_token || ''}`,
          },
          body: JSON.stringify({
            recipientEmail: email.trim(),
            email:          email.trim(),
            name:           name.trim() || undefined,
            role,
            companyCode,
            companyName,
            inviteLink,
            inviterName:    profile?.full_name || profile?.company_name || profile?.email || 'A team member',
            senderName:     profile?.full_name || profile?.company_name || '',
          }),
        }
      );
    } catch { /* non-critical */ }

    setSending(false);
    setSuccess(true);
    onInvited();
  }

  const inputStyle = {
    width: '100%', padding: '10px 12px', borderRadius: 7, fontSize: 13,
    background: 'var(--bg2)', border: '1px solid var(--border)', color: 'var(--text)',
    outline: 'none', boxSizing: 'border-box', fontFamily: 'var(--font)',
  };

  return (
    <div
      style={{
        position: 'fixed', inset: 0, background: 'rgba(0,0,0,0.65)',
        display: 'flex', alignItems: 'center', justifyContent: 'center',
        zIndex: 1000, padding: 16,
      }}
      onClick={e => { if (e.target === e.currentTarget) onClose(); }}
    >
      <div style={{
        background: 'var(--bg2)', border: '1px solid var(--border)',
        borderRadius: 12, padding: 32, width: '100%', maxWidth: 480,
        boxShadow: '0 24px 64px rgba(0,0,0,0.4)',
      }}>
        {success ? (
          <div style={{ textAlign: 'center' }}>
            <div style={{
              width: 56, height: 56, borderRadius: '50%', margin: '0 auto 16px',
              background: 'var(--green-bg)', border: '1px solid rgba(29,184,122,0.4)',
              display: 'flex', alignItems: 'center', justifyContent: 'center',
            }}>
              <svg width="24" height="24" viewBox="0 0 24 24" fill="none" stroke="var(--green)" strokeWidth="2.2" strokeLinecap="round" strokeLinejoin="round">
                <path d="M20 6L9 17l-5-5"/>
              </svg>
            </div>
            <div style={{ fontSize: 18, fontWeight: 700, marginBottom: 8 }}>Invite Sent!</div>
            <div style={{ fontSize: 13, color: 'var(--text3)', marginBottom: 24, lineHeight: 1.6 }}>
              An invitation was sent to <strong style={{ color: 'var(--text)' }}>{email}</strong>.
              They will receive an email with instructions to join {companyName}.
            </div>
            <button
              onClick={onClose}
              style={{
                width: '100%', padding: '11px', borderRadius: 8, fontSize: 13,
                fontWeight: 600, background: 'var(--accent)', color: '#fff',
                border: 'none', cursor: 'pointer', fontFamily: 'var(--font)',
              }}
            >
              Done
            </button>
          </div>
        ) : (
          <>
            <div style={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', marginBottom: 24 }}>
              <div style={{ fontSize: 18, fontWeight: 700 }}>Invite Team Member</div>
              <button
                onClick={onClose}
                style={{ background: 'none', border: 'none', cursor: 'pointer', color: 'var(--text3)', padding: 4 }}
              >
                <svg width="18" height="18" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
                  <line x1="18" y1="6" x2="6" y2="18"/><line x1="6" y1="6" x2="18" y2="18"/>
                </svg>
              </button>
            </div>

            {error && (
              <div style={{
                background: 'var(--red-bg)', border: '1px solid rgba(239,68,68,0.3)',
                borderRadius: 7, padding: '10px 12px', fontSize: 12, color: 'var(--red)', marginBottom: 16,
              }}>
                {error}
              </div>
            )}

            <form onSubmit={handleSubmit}>
              <div style={{ marginBottom: 14 }}>
                <label style={{ display: 'block', fontSize: 12, color: 'var(--text3)', marginBottom: 5 }}>Full Name</label>
                <input
                  style={inputStyle}
                  type="text"
                  value={name}
                  onChange={e => setName(e.target.value)}
                  placeholder="Jane Smith"
                  autoFocus
                />
              </div>

              <div style={{ marginBottom: 14 }}>
                <label style={{ display: 'block', fontSize: 12, color: 'var(--text3)', marginBottom: 5 }}>Email Address *</label>
                <input
                  style={inputStyle}
                  type="email"
                  value={email}
                  onChange={e => setEmail(e.target.value)}
                  placeholder="colleague@company.com"
                  required
                />
              </div>

              <div style={{ marginBottom: 24 }}>
                <label style={{ display: 'block', fontSize: 12, color: 'var(--text3)', marginBottom: 8 }}>Role</label>
                <div style={{ display: 'flex', flexDirection: 'column', gap: 8 }}>
                  {[
                    { value: 'admin',  label: 'Admin',  desc: 'Can manage controls, evidence, and invite members' },
                    { value: 'member', label: 'Member', desc: 'Can view and update controls, upload evidence' },
                  ].map(r => (
                    <label
                      key={r.value}
                      style={{
                        display: 'flex', gap: 12, padding: '11px 14px', borderRadius: 8, cursor: 'pointer',
                        border: `1px solid ${role === r.value ? 'rgba(45,125,210,0.4)' : 'var(--border)'}`,
                        background: role === r.value ? 'var(--blue-bg)' : 'var(--surface)',
                        transition: 'all 0.15s',
                      }}
                    >
                      <input
                        type="radio" name="role" value={r.value}
                        checked={role === r.value}
                        onChange={() => setRole(r.value)}
                        style={{ marginTop: 3, flexShrink: 0 }}
                      />
                      <div>
                        <div style={{ display: 'flex', alignItems: 'center', gap: 7, marginBottom: 3 }}>
                          <span style={{ fontSize: 13, fontWeight: 600, color: 'var(--text)' }}>{r.label}</span>
                          <RoleBadge role={r.value} />
                        </div>
                        <div style={{ fontSize: 12, color: 'var(--text3)', lineHeight: 1.5 }}>{r.desc}</div>
                      </div>
                    </label>
                  ))}
                </div>
              </div>

              <button
                type="submit"
                disabled={sending || seatsFull}
                style={{
                  width: '100%', padding: '12px', borderRadius: 8, fontSize: 14,
                  fontWeight: 600, background: 'var(--accent)', color: '#fff',
                  border: 'none', cursor: sending || seatsFull ? 'not-allowed' : 'pointer',
                  opacity: sending || seatsFull ? 0.7 : 1, marginBottom: 10,
                  fontFamily: 'var(--font)',
                }}
              >
                {sending ? 'Sending...' : 'Send Invite'}
              </button>
              <button
                type="button"
                onClick={onClose}
                style={{
                  width: '100%', padding: '10px', background: 'none', border: 'none',
                  color: 'var(--text3)', fontSize: 13, cursor: 'pointer', fontFamily: 'var(--font)',
                }}
              >
                Cancel
              </button>
            </form>
          </>
        )}
      </div>
    </div>
  );
}

// ── Remove Confirmation Modal ──────────────────────────────────────────────────

function RemoveModal({ member, onConfirm, onClose, removing }) {
  const displayName = member.full_name || member.email || member.invited_email || 'this member';

  return (
    <div
      style={{
        position: 'fixed', inset: 0, background: 'rgba(0,0,0,0.65)',
        display: 'flex', alignItems: 'center', justifyContent: 'center',
        zIndex: 1000, padding: 16,
      }}
      onClick={e => { if (e.target === e.currentTarget) onClose(); }}
    >
      <div style={{
        background: 'var(--bg2)', border: '1px solid var(--border)',
        borderRadius: 12, padding: 32, width: '100%', maxWidth: 440,
        boxShadow: '0 24px 64px rgba(0,0,0,0.4)',
      }}>
        <div style={{
          width: 48, height: 48, borderRadius: '50%', margin: '0 0 16px',
          background: 'var(--red-bg)', border: '1px solid rgba(239,68,68,0.3)',
          display: 'flex', alignItems: 'center', justifyContent: 'center',
        }}>
          <svg width="20" height="20" viewBox="0 0 24 24" fill="none" stroke="var(--red)" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
            <polyline points="3 6 5 6 21 6"/><path d="M19 6l-1 14a2 2 0 01-2 2H8a2 2 0 01-2-2L5 6"/>
            <path d="M10 11v6"/><path d="M14 11v6"/><path d="M9 6V4h6v2"/>
          </svg>
        </div>
        <div style={{ fontSize: 17, fontWeight: 700, marginBottom: 10 }}>
          Remove {displayName}?
        </div>
        <div style={{ fontSize: 13, color: 'var(--text3)', lineHeight: 1.65, marginBottom: 24 }}>
          They will immediately lose access to all company compliance data. This cannot be undone.
        </div>
        <div style={{ display: 'flex', gap: 10 }}>
          <button
            onClick={onClose}
            style={{
              flex: 1, padding: '10px', borderRadius: 8, fontSize: 13,
              background: 'none', border: '1px solid var(--border)', color: 'var(--text)',
              cursor: 'pointer', fontFamily: 'var(--font)',
            }}
          >
            Cancel
          </button>
          <button
            onClick={onConfirm}
            disabled={removing}
            style={{
              flex: 1, padding: '10px', borderRadius: 8, fontSize: 13, fontWeight: 600,
              background: 'var(--red)', color: '#fff', border: 'none',
              cursor: removing ? 'not-allowed' : 'pointer', opacity: removing ? 0.7 : 1,
              fontFamily: 'var(--font)',
            }}
          >
            {removing ? 'Removing...' : 'Remove Member'}
          </button>
        </div>
      </div>
    </div>
  );
}

// ── Three-dot Menu ─────────────────────────────────────────────────────────────

function ThreeDotMenu({ onChangeRole, onRemove }) {
  const [open, setOpen] = useState(false);
  const ref = useRef(null);

  useEffect(() => {
    function handler(e) {
      if (ref.current && !ref.current.contains(e.target)) setOpen(false);
    }
    document.addEventListener('mousedown', handler);
    return () => document.removeEventListener('mousedown', handler);
  }, []);

  return (
    <div ref={ref} style={{ position: 'relative', flexShrink: 0 }}>
      <button
        onClick={() => setOpen(o => !o)}
        style={{
          width: 30, height: 30, borderRadius: 6, background: 'none',
          border: '1px solid transparent', cursor: 'pointer',
          display: 'flex', alignItems: 'center', justifyContent: 'center',
          color: 'var(--text3)', transition: 'all 0.15s',
        }}
        onMouseEnter={e => { e.currentTarget.style.background = 'var(--surface2)'; e.currentTarget.style.borderColor = 'var(--border)'; }}
        onMouseLeave={e => { if (!open) { e.currentTarget.style.background = 'none'; e.currentTarget.style.borderColor = 'transparent'; } }}
      >
        <svg width="16" height="16" viewBox="0 0 24 24" fill="currentColor">
          <circle cx="12" cy="5" r="1.5"/><circle cx="12" cy="12" r="1.5"/><circle cx="12" cy="19" r="1.5"/>
        </svg>
      </button>
      {open && (
        <div style={{
          position: 'absolute', right: 0, top: 34, zIndex: 200,
          background: 'var(--bg2)', border: '1px solid var(--border)',
          borderRadius: 8, boxShadow: '0 8px 24px rgba(0,0,0,0.25)',
          minWidth: 160, overflow: 'hidden',
        }}>
          <button
            onClick={() => { setOpen(false); onChangeRole(); }}
            style={{
              width: '100%', padding: '10px 14px', background: 'none', border: 'none',
              cursor: 'pointer', fontSize: 13, color: 'var(--text)', textAlign: 'left',
              display: 'flex', alignItems: 'center', gap: 8, fontFamily: 'var(--font)',
            }}
            onMouseEnter={e => e.currentTarget.style.background = 'var(--surface)'}
            onMouseLeave={e => e.currentTarget.style.background = 'none'}
          >
            <svg width="13" height="13" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
              <path d="M17 21v-2a4 4 0 0 0-4-4H5a4 4 0 0 0-4 4v2"/><circle cx="9" cy="7" r="4"/>
              <path d="M23 21v-2a4 4 0 0 0-3-3.87"/><path d="M16 3.13a4 4 0 0 1 0 7.75"/>
            </svg>
            Change Role
          </button>
          <div style={{ height: 1, background: 'var(--border)' }} />
          <button
            onClick={() => { setOpen(false); onRemove(); }}
            style={{
              width: '100%', padding: '10px 14px', background: 'none', border: 'none',
              cursor: 'pointer', fontSize: 13, color: 'var(--red)', textAlign: 'left',
              display: 'flex', alignItems: 'center', gap: 8, fontFamily: 'var(--font)',
            }}
            onMouseEnter={e => e.currentTarget.style.background = 'var(--red-bg)'}
            onMouseLeave={e => e.currentTarget.style.background = 'none'}
          >
            <svg width="13" height="13" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
              <path d="M17 21v-2a4 4 0 0 0-4-4H5a4 4 0 0 0-4 4v2"/><circle cx="9" cy="7" r="4"/>
              <line x1="17" y1="8" x2="23" y2="14"/><line x1="23" y1="8" x2="17" y2="14"/>
            </svg>
            Remove from Team
          </button>
        </div>
      )}
    </div>
  );
}

// ── Member Row ────────────────────────────────────────────────────────────────

function MemberRow({ member, isCurrentUser, canManage, roleDropdownOpenFor, setRoleDropdownOpenFor, onRoleChange, onRemove, formatDate }) {
  const role  = member.role || 'member';
  const name  = member.full_name || member.email || 'Team Member';
  const email = member.email || '';

  return (
    <div
      style={{
        padding: '14px 20px', borderBottom: '1px solid var(--border)',
        display: 'flex', alignItems: 'center', gap: 14, transition: 'background 0.1s',
      }}
      onMouseEnter={e => e.currentTarget.style.background = 'var(--surface)'}
      onMouseLeave={e => e.currentTarget.style.background = 'transparent'}
    >
      <Avatar name={name} role={role} size={40} />

      <div style={{ flex: 1, minWidth: 0 }}>
        <div style={{
          fontSize: 13, fontWeight: 600, color: 'var(--text)',
          overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap',
        }}>
          {name}
          {isCurrentUser && (
            <span style={{ marginLeft: 6, fontSize: 10, color: 'var(--text3)', fontWeight: 400 }}>(you)</span>
          )}
        </div>
        <div style={{
          fontSize: 11, color: 'var(--text3)', marginTop: 2,
          overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap',
        }}>
          {email}
        </div>
      </div>

      {/* Role: inline select or badge */}
      {roleDropdownOpenFor === member.id ? (
        <select
          value={role}
          autoFocus
          onChange={e => onRoleChange(member.id, e.target.value)}
          onBlur={() => setRoleDropdownOpenFor(null)}
          style={{
            padding: '5px 10px', borderRadius: 6, fontSize: 12,
            background: 'var(--bg2)', border: '1px solid var(--accent)',
            color: 'var(--text)', fontFamily: 'var(--font)', cursor: 'pointer',
          }}
        >
          <option value="admin">Admin</option>
          <option value="member">Member</option>
        </select>
      ) : (
        <RoleBadge role={role} />
      )}

      <div style={{ fontSize: 11, color: 'var(--text3)', whiteSpace: 'nowrap', flexShrink: 0 }}>
        Joined {formatDate(member.created_at)}
      </div>

      {canManage && role !== 'owner' && !isCurrentUser ? (
        <ThreeDotMenu
          onChangeRole={() => setRoleDropdownOpenFor(member.id)}
          onRemove={onRemove}
        />
      ) : (
        <div style={{ width: 30, flexShrink: 0 }} />
      )}
    </div>
  );
}

// ── Main Component ─────────────────────────────────────────────────────────────

export default function TeamManagement({ session, profile, companyId, effectivePlan, companyName, onUpgrade }) {
  const [members,              setMembers]              = useState([]);
  const [pendingInvites,       setPendingInvites]       = useState([]);
  const [loading,              setLoading]              = useState(true);
  const [error,                setError]                = useState(null);
  const [showInviteModal,      setShowInviteModal]      = useState(false);
  const [removingMember,       setRemovingMember]       = useState(null);
  const [removing,             setRemoving]             = useState(false);
  const [toast,                setToast]                = useState(null);
  const [roleDropdownOpenFor,  setRoleDropdownOpenFor]  = useState(null);
  const [resendingId,          setResendingId]          = useState(null);
  const [revokingId,           setRevokingId]           = useState(null);

  // ── Derived state ────────────────────────────────────────────────────────────
  const myRole    = profile?.role || 'member';
  const canManage = myRole === 'owner';

  const seatCount = members.length;
  const seatLimit = effectivePlan === 'professional' ? 3 : Infinity;
  const atSeatLimit = effectivePlan === 'professional' && seatCount >= seatLimit;

  // ── Helpers ──────────────────────────────────────────────────────────────────
  function showToast(message, type = 'success') {
    setToast({ message, type });
  }

  function formatDate(dateStr) {
    if (!dateStr) return '';
    try {
      return new Date(dateStr).toLocaleDateString('en-US', { month: 'short', day: 'numeric', year: 'numeric' });
    } catch {
      return '';
    }
  }

  // ── Data loading ─────────────────────────────────────────────────────────────
  const loadData = useCallback(async () => {
    if (!companyId) { setLoading(false); return; }
    setLoading(true);
    setError(null);

    // 1. Fetch company owner_id
    const { data: co, error: coErr } = await supabase
      .from('companies')
      .select('owner_id, company_code')
      .eq('id', companyId)
      .maybeSingle();

    if (coErr) {
      setError(coErr.message);
      setLoading(false);
      return;
    }

    // 2. Fetch all active company_members with profile info
    const { data: memberRows, error: memberErr } = await supabase
      .from('company_members')
      .select('id, user_id, role, status, invite_accepted, invited_email, created_at, accepted_at')
      .eq('company_id', companyId)
      .order('created_at', { ascending: true });

    if (memberErr) {
      setError(memberErr.message);
      setLoading(false);
      return;
    }

    // Active members = accepted and/or status active
    const activeRows = (memberRows || []).filter(m => m.invite_accepted === true || m.status === 'active');

    // 3. Fetch profile data for active members who have user_ids
    const userIds = activeRows.filter(m => m.user_id).map(m => m.user_id);
    let profileMap = {};
    if (userIds.length > 0) {
      const { data: profileData } = await supabase
        .from('profiles')
        .select('id, email, full_name, company_name, created_at')
        .in('id', userIds);
      (profileData || []).forEach(p => { profileMap[p.id] = p; });
    }

    // 4. Fetch owner profile
    let ownerProfileData = null;
    if (co?.owner_id) {
      const { data: op } = await supabase
        .from('profiles')
        .select('id, email, full_name, company_name, created_at')
        .eq('id', co.owner_id)
        .maybeSingle();
      ownerProfileData = op;
    }

    // 5. Build unified member list — owner first, then members
    const ownerEntry = {
      id:         co?.owner_id || 'owner',
      user_id:    co?.owner_id || null,
      role:       'owner',
      created_at: ownerProfileData?.created_at || null,
      full_name:  ownerProfileData?.full_name || ownerProfileData?.company_name || ownerProfileData?.email || 'Account Owner',
      email:      ownerProfileData?.email || '',
    };

    const memberEntries = activeRows
      .filter(m => m.user_id !== co?.owner_id) // skip if owner is also in members table
      .map(m => {
        const p = m.user_id ? (profileMap[m.user_id] || null) : null;
        return {
          id:         m.id,
          user_id:    m.user_id,
          role:       m.role || 'member',
          created_at: m.accepted_at || m.created_at,
          full_name:  p?.full_name || p?.company_name || p?.email || m.invited_email || 'Team Member',
          email:      p?.email || m.invited_email || '',
        };
      });

    setMembers([ownerEntry, ...memberEntries]);

    // 6. Fetch pending invites (owner only)
    if (canManage) {
      const invites = await getCompanyInvites(companyId);
      setPendingInvites(invites);
    }

    setLoading(false);
  }, [companyId, canManage]);

  useEffect(() => {
    loadData();
  }, [loadData]);

  // ── Invite ────────────────────────────────────────────────────────────────────
  async function handleInvited() {
    if (!companyId) return;
    const invites = await getCompanyInvites(companyId);
    setPendingInvites(invites);
  }

  // ── Role change ───────────────────────────────────────────────────────────────
  async function handleRoleChange(memberId, newRole) {
    const { error: err } = await supabase
      .from('company_members')
      .update({ role: newRole })
      .eq('id', memberId);

    if (!err) {
      setMembers(prev => prev.map(m => m.id === memberId ? { ...m, role: newRole } : m));
      setRoleDropdownOpenFor(null);
      showToast(`Role updated to ${newRole.charAt(0).toUpperCase() + newRole.slice(1)}`);
    } else {
      showToast('Failed to update role.', 'error');
    }
  }

  // ── Remove member ─────────────────────────────────────────────────────────────
  async function handleRemoveMember() {
    if (!removingMember) return;
    setRemoving(true);

    const { error: err } = await supabase
      .from('company_members')
      .delete()
      .eq('id', removingMember.id);

    if (!err) {
      setMembers(prev => prev.filter(m => m.id !== removingMember.id));
      setRemovingMember(null);
      showToast(`${removingMember.full_name || removingMember.email || 'Member'} removed from team.`);
    } else {
      showToast('Failed to remove member.', 'error');
    }
    setRemoving(false);
  }

  // ── Resend / Revoke invite ────────────────────────────────────────────────────
  async function handleResendInvite(invite) {
    setResendingId(invite.id);
    const result = await createCompanyInvite(companyId, invite.email, invite.role, session?.user?.id, []);
    if (result) {
      try {
        const { data: co } = await supabase
          .from('companies')
          .select('company_code')
          .eq('id', companyId)
          .single();
        await fetch(
          `${process.env.REACT_APP_SUPABASE_URL}/functions/v1/send-invite`,
          {
            method: 'POST',
            headers: {
              'Content-Type': 'application/json',
              'Authorization': `Bearer ${session?.access_token || ''}`,
            },
            body: JSON.stringify({
              recipientEmail: invite.email,
              email:          invite.email,
              role:           invite.role,
              companyCode:    co?.company_code || '',
              companyName,
              inviteLink:     `${window.location.origin}?company_invite=${result.token}`,
              inviterName:    profile?.full_name || profile?.company_name || profile?.email || 'A team member',
            }),
          }
        );
      } catch { /* non-critical */ }
      showToast(`Invite resent to ${invite.email}.`);
    } else {
      showToast('Failed to resend invite.', 'error');
    }
    setResendingId(null);
  }

  async function handleRevokeInvite(invite) {
    setRevokingId(invite.id);
    const ok = await revokeCompanyInvite(invite.id);
    if (ok) {
      setPendingInvites(prev => prev.filter(i => i.id !== invite.id));
      showToast('Invite cancelled.');
    } else {
      showToast('Failed to cancel invite.', 'error');
    }
    setRevokingId(null);
  }

  // ── Early returns ─────────────────────────────────────────────────────────────

  if (!companyId) {
    return (
      <div style={{ padding: 40, textAlign: 'center', color: 'var(--text3)', fontSize: 13 }}>
        You are not part of a company account.{' '}
        <button
          onClick={onUpgrade}
          style={{
            background: 'none', border: 'none', color: 'var(--accent)',
            cursor: 'pointer', fontSize: 13, fontFamily: 'var(--font)',
          }}
        >
          Set up your team
        </button>
      </div>
    );
  }

  if (loading) {
    return (
      <div style={{ display: 'flex', alignItems: 'center', justifyContent: 'center', height: 200 }}>
        <div style={{ fontSize: 13, color: 'var(--text3)' }}>Loading team members...</div>
      </div>
    );
  }

  if (error) {
    return (
      <div style={{ padding: 24, color: 'var(--red)', fontSize: 13 }}>
        Failed to load team members. {error}
      </div>
    );
  }

  // ── Derived display values ────────────────────────────────────────────────────
  const displayName     = companyName || 'Your Company';
  const seatLimitLabel  = seatLimit === Infinity ? 'unlimited' : String(seatLimit);
  const seatsLabel      = seatLimit === Infinity
    ? `${seatCount} members`
    : `${seatCount} of ${seatLimitLabel} seats`;

  const nonOwnerMembers = members.filter(m => m.role !== 'owner');
  const hasOnlyOwner    = nonOwnerMembers.length === 0;

  return (
    <div style={{ padding: 28, overflowY: 'auto', height: '100%' }}>
      {/* Header */}
      <div style={{
        display: 'flex', alignItems: 'flex-start', justifyContent: 'space-between',
        marginBottom: 20, flexWrap: 'wrap', gap: 12,
      }}>
        <div>
          <div style={{ fontSize: 22, fontWeight: 700, marginBottom: 4 }}>Team Management</div>
          <div style={{ fontSize: 13, color: 'var(--text3)' }}>
            {displayName} &middot; {seatsLabel}
          </div>
        </div>
        {canManage && !atSeatLimit && (
          <button
            onClick={() => setShowInviteModal(true)}
            style={{
              display: 'flex', alignItems: 'center', gap: 8,
              padding: '9px 18px', borderRadius: 8, fontSize: 13, fontWeight: 600,
              background: 'var(--accent)', color: '#fff',
              border: '1px solid var(--accent)',
              cursor: 'pointer', fontFamily: 'var(--font)', transition: 'all 0.15s',
            }}
          >
            <svg width="15" height="15" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2.2" strokeLinecap="round" strokeLinejoin="round">
              <path d="M16 21v-2a4 4 0 0 0-4-4H5a4 4 0 0 0-4 4v2"/>
              <circle cx="8.5" cy="7" r="4"/>
              <line x1="20" y1="8" x2="20" y2="14"/>
              <line x1="23" y1="11" x2="17" y2="11"/>
            </svg>
            Invite Member
          </button>
        )}
      </div>

      {/* Seat limit banner */}
      {effectivePlan === 'professional' && atSeatLimit && (
        <div style={{
          background: 'var(--amber-bg)', border: '1px solid rgba(245,158,11,0.35)',
          borderRadius: 8, padding: '12px 16px', marginBottom: 20,
          display: 'flex', alignItems: 'center', justifyContent: 'space-between', gap: 12,
          flexWrap: 'wrap',
        }}>
          <div style={{ display: 'flex', alignItems: 'center', gap: 10 }}>
            <svg width="16" height="16" viewBox="0 0 24 24" fill="none" stroke="var(--amber)" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round" style={{ flexShrink: 0 }}>
              <path d="M10.29 3.86L1.82 18a2 2 0 0 0 1.71 3h16.94a2 2 0 0 0 1.71-3L13.71 3.86a2 2 0 0 0-3.42 0z"/>
              <line x1="12" y1="9" x2="12" y2="13"/><line x1="12" y1="17" x2="12.01" y2="17"/>
            </svg>
            <span style={{ fontSize: 13, color: 'var(--text)' }}>
              You've reached your 3-seat limit on the Professional plan.
            </span>
          </div>
          <button
            onClick={onUpgrade}
            style={{
              padding: '6px 14px', borderRadius: 6, fontSize: 12, fontWeight: 600,
              background: 'var(--accent)', color: '#fff', border: 'none',
              cursor: 'pointer', flexShrink: 0, fontFamily: 'var(--font)',
            }}
          >
            Upgrade to Business
          </button>
        </div>
      )}

      {/* Members Section */}
      <div style={{
        background: 'var(--bg2)', border: '1px solid var(--border)',
        borderRadius: 10, overflow: 'hidden', marginBottom: 24,
      }}>
        <div style={{
          padding: '14px 20px', borderBottom: '1px solid var(--border)',
          display: 'flex', alignItems: 'center', justifyContent: 'space-between',
        }}>
          <div style={{ fontSize: 13, fontWeight: 600 }}>Active Members</div>
          <span style={{
            fontSize: 10, fontFamily: 'var(--mono)', color: 'var(--text3)',
            background: 'var(--surface2)', padding: '2px 8px', borderRadius: 10,
            border: '1px solid var(--border)',
          }}>
            {seatCount} {seatCount === 1 ? 'member' : 'members'}
          </span>
        </div>

        {/* Member rows */}
        {members.map(m => (
          <MemberRow
            key={m.id}
            member={m}
            isCurrentUser={m.user_id === session?.user?.id}
            canManage={canManage}
            roleDropdownOpenFor={roleDropdownOpenFor}
            setRoleDropdownOpenFor={setRoleDropdownOpenFor}
            onRoleChange={handleRoleChange}
            onRemove={() => setRemovingMember(m)}
            formatDate={formatDate}
          />
        ))}

        {/* Empty state — only owner, no other members */}
        {hasOnlyOwner && canManage && (
          <div style={{ padding: '40px 24px', textAlign: 'center' }}>
            <div style={{
              width: 56, height: 56, borderRadius: '50%', margin: '0 auto 16px',
              background: 'var(--surface)', border: '1px solid var(--border)',
              display: 'flex', alignItems: 'center', justifyContent: 'center',
            }}>
              <svg width="24" height="24" viewBox="0 0 24 24" fill="none" stroke="var(--text3)" strokeWidth="1.8" strokeLinecap="round" strokeLinejoin="round">
                <path d="M17 21v-2a4 4 0 0 0-4-4H5a4 4 0 0 0-4 4v2"/>
                <circle cx="9" cy="7" r="4"/>
                <path d="M23 21v-2a4 4 0 0 0-5-3.87"/>
                <path d="M16 3.13a4 4 0 0 1 0 7.75"/>
              </svg>
            </div>
            <div style={{ fontSize: 15, fontWeight: 600, color: 'var(--text)', marginBottom: 8 }}>No team members yet</div>
            <div style={{ fontSize: 13, color: 'var(--text3)', marginBottom: 24, lineHeight: 1.6 }}>
              Invite your first team member to collaborate on compliance.
            </div>
            <button
              onClick={() => setShowInviteModal(true)}
              style={{
                display: 'inline-flex', alignItems: 'center', gap: 8,
                padding: '12px 24px', borderRadius: 8, fontSize: 14, fontWeight: 600,
                background: 'var(--accent)', color: '#fff', border: 'none',
                cursor: 'pointer', fontFamily: 'var(--font)',
              }}
            >
              <svg width="14" height="14" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2.2" strokeLinecap="round" strokeLinejoin="round">
                <path d="M16 21v-2a4 4 0 0 0-4-4H5a4 4 0 0 0-4 4v2"/>
                <circle cx="8.5" cy="7" r="4"/>
                <line x1="20" y1="8" x2="20" y2="14"/>
                <line x1="23" y1="11" x2="17" y2="11"/>
              </svg>
              Invite Team Member
            </button>
          </div>
        )}
      </div>

      {/* Pending Invites Section */}
      {canManage && pendingInvites.length > 0 && (
        <div style={{
          background: 'var(--bg2)', border: '1px solid var(--border)',
          borderRadius: 10, overflow: 'hidden', marginBottom: 24,
        }}>
          <div style={{
            padding: '14px 20px', borderBottom: '1px solid var(--border)',
            display: 'flex', alignItems: 'center', justifyContent: 'space-between',
          }}>
            <div style={{ fontSize: 13, fontWeight: 600 }}>Pending Invites</div>
            <span style={{
              fontSize: 10, fontFamily: 'var(--mono)', color: 'var(--amber)',
              background: 'var(--amber-bg)', padding: '2px 8px', borderRadius: 10,
              border: '1px solid rgba(245,158,11,0.3)',
            }}>
              {pendingInvites.length} pending
            </span>
          </div>
          {pendingInvites.map(invite => (
            <div
              key={invite.id}
              style={{
                padding: '14px 20px', borderBottom: '1px solid var(--border)',
                display: 'flex', alignItems: 'center', gap: 14,
              }}
            >
              <div style={{
                width: 40, height: 40, borderRadius: '50%', flexShrink: 0,
                background: 'var(--surface)', border: '1px solid var(--border)',
                display: 'flex', alignItems: 'center', justifyContent: 'center',
              }}>
                <svg width="16" height="16" viewBox="0 0 24 24" fill="none" stroke="var(--text3)" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
                  <path d="M20 21v-2a4 4 0 0 0-4-4H8a4 4 0 0 0-4 4v2"/>
                  <circle cx="12" cy="7" r="4"/>
                </svg>
              </div>
              <div style={{ flex: 1, minWidth: 0 }}>
                <div style={{
                  fontSize: 13, fontWeight: 500, color: 'var(--text)',
                  overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap',
                }}>
                  {invite.email}
                </div>
                <div style={{ fontSize: 11, color: 'var(--text3)', marginTop: 2 }}>
                  Invited {formatDate(invite.created_at)}
                </div>
              </div>
              <RoleBadge role={invite.role || 'member'} />
              <span style={{
                fontSize: 10, fontWeight: 500, padding: '2px 7px', borderRadius: 6,
                color: 'var(--amber)', background: 'var(--amber-bg)',
                border: '1px solid rgba(245,158,11,0.3)', flexShrink: 0,
              }}>
                Pending
              </span>
              <div style={{ display: 'flex', gap: 8, flexShrink: 0 }}>
                <button
                  onClick={() => handleResendInvite(invite)}
                  disabled={resendingId === invite.id}
                  style={{
                    padding: '5px 12px', borderRadius: 6, fontSize: 11, fontWeight: 600,
                    background: 'var(--accent)', color: '#fff', border: 'none',
                    cursor: resendingId === invite.id ? 'not-allowed' : 'pointer',
                    opacity: resendingId === invite.id ? 0.7 : 1, fontFamily: 'var(--font)',
                  }}
                >
                  {resendingId === invite.id ? '...' : 'Resend'}
                </button>
                <button
                  onClick={() => handleRevokeInvite(invite)}
                  disabled={revokingId === invite.id}
                  style={{
                    width: 28, height: 28, borderRadius: 6, background: 'none',
                    border: '1px solid var(--border)', cursor: revokingId === invite.id ? 'not-allowed' : 'pointer',
                    display: 'flex', alignItems: 'center', justifyContent: 'center',
                    color: 'var(--text3)', transition: 'all 0.15s',
                  }}
                  onMouseEnter={e => { e.currentTarget.style.background = 'var(--red-bg)'; e.currentTarget.style.color = 'var(--red)'; e.currentTarget.style.borderColor = 'rgba(239,68,68,0.3)'; }}
                  onMouseLeave={e => { e.currentTarget.style.background = 'none'; e.currentTarget.style.color = 'var(--text3)'; e.currentTarget.style.borderColor = 'var(--border)'; }}
                >
                  <svg width="12" height="12" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2.2" strokeLinecap="round" strokeLinejoin="round">
                    <line x1="18" y1="6" x2="6" y2="18"/><line x1="6" y1="6" x2="18" y2="18"/>
                  </svg>
                </button>
              </div>
            </div>
          ))}
        </div>
      )}

      {/* Read-only note for non-owners */}
      {!canManage && (
        <div style={{
          background: 'var(--surface)', border: '1px solid var(--border)',
          borderRadius: 8, padding: '12px 16px',
          display: 'flex', alignItems: 'center', gap: 10,
          fontSize: 12, color: 'var(--text3)',
        }}>
          <svg width="14" height="14" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round" style={{ flexShrink: 0 }}>
            <rect x="3" y="11" width="18" height="11" rx="2"/><path d="M7 11V7a5 5 0 0110 0v4"/>
          </svg>
          Contact your account owner to manage team members.
        </div>
      )}

      {/* Modals */}
      {showInviteModal && (
        <InviteModal
          companyId={companyId}
          companyName={displayName}
          session={session}
          profile={profile}
          seatsFull={atSeatLimit}
          onClose={() => setShowInviteModal(false)}
          onInvited={handleInvited}
        />
      )}
      {removingMember && (
        <RemoveModal
          member={removingMember}
          onConfirm={handleRemoveMember}
          onClose={() => setRemovingMember(null)}
          removing={removing}
        />
      )}
      {toast && (
        <Toast
          message={toast.message}
          type={toast.type}
          onDismiss={() => setToast(null)}
        />
      )}
    </div>
  );
}
